class PriceRangeSlider extends HTMLElement {
  constructor() {
    super();
    this.min = 10;
    this.max = 100000;
    this.gap = 100;
  }

  connectedCallback() {
    console.log("price-range-slider");
    this.minInput = this.querySelector("#price-min");
    this.maxInput = this.querySelector("#price-max");
    this.rangeMin = this.querySelector("#range-min");
    this.rangeMax = this.querySelector("#range-max");
    this.mobile_minInput = this.querySelector("#Mobile-Filter-price-min");
    this.mobile_maxInput = this.querySelector("#Mobile-Filter-price-max");
    this.mobile_rangeMin = this.querySelector("#Mobile-Filter-range-min");
    this.mobile_rangeMax = this.querySelector("#Mobile-Filter-range-max");

    this.attachEventListeners();
    this.syncInputsFromSlider();
    this.syncInputsFromSliderMobile();
  }

  formatEuro(val) {
    return (
      "€" +
      Number(val).toLocaleString("nl-NL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  syncInputsFromSlider = () => {
    if (!this.rangeMin || !this.rangeMax || !this.minInput || !this.maxInput)
      return;
    let minVal = Math.min(
      Number(this.rangeMin.value),
      Number(this.rangeMax.value) - this.gap,
    );
    let maxVal = Math.max(
      Number(this.rangeMax.value),
      Number(this.rangeMin.value) + this.gap,
    );
    this.rangeMin.value = minVal;
    this.rangeMax.value = maxVal;
    this.minInput.value = minVal;
    this.maxInput.value = maxVal;
    this.minInput.setAttribute("value", minVal);
    this.maxInput.setAttribute("value", maxVal);
  };

  syncInputsFromSliderMobile = () => {
    if (
      !this.mobile_rangeMin ||
      !this.mobile_rangeMax ||
      !this.mobile_minInput ||
      !this.mobile_maxInput
    )
      return;
    let minVal = Math.min(
      Number(this.mobile_rangeMin.value),
      Number(this.mobile_rangeMax.value) - this.gap,
    );
    let maxVal = Math.max(
      Number(this.mobile_rangeMax.value),
      Number(this.mobile_rangeMin.value) + this.gap,
    );
    this.mobile_rangeMin.value = minVal;
    this.mobile_rangeMax.value = maxVal;
    this.mobile_minInput.value = minVal;
    this.mobile_maxInput.value = maxVal;
    this.mobile_minInput.setAttribute("value", minVal);
    this.mobile_maxInput.setAttribute("value", maxVal);
  };

  syncSliderFromInputs = () => {
    let minVal = Math.max(
      this.min,
      Math.min(
        Number(this.minInput.value),
        Number(this.maxInput.value) - this.gap,
      ),
    );
    let maxVal = Math.min(
      this.max,
      Math.max(
        Number(this.maxInput.value),
        Number(this.minInput.value) + this.gap,
      ),
    );
    this.rangeMin.value = minVal;
    this.rangeMax.value = maxVal;
    this.minInput.value = minVal;
    this.maxInput.value = maxVal;
  };

  syncSliderFromInputsMobile = () => {
    let minVal = Math.max(
      this.min,
      Math.min(
        Number(this.mobile_minInput.value),
        Number(this.mobile_maxInput.value) - this.gap,
      ),
    );
    let maxVal = Math.min(
      this.max,
      Math.max(
        Number(this.mobile_maxInput.value),
        Number(this.mobile_minInput.value) + this.gap,
      ),
    );
    this.mobile_rangeMin.value = minVal;
    this.mobile_rangeMax.value = maxVal;
    this.mobile_minInput.value = minVal;
    this.mobile_maxInput.value = maxVal;
  };

  attachEventListeners() {
    this.rangeMin?.addEventListener("input", this.syncInputsFromSlider);
    this.rangeMax?.addEventListener("input", this.syncInputsFromSlider);
    this.minInput?.addEventListener("change", this.syncSliderFromInputs);
    this.maxInput?.addEventListener("change", this.syncSliderFromInputs);
    this.mobile_rangeMin?.addEventListener(
      "input",
      this.syncInputsFromSliderMobile,
    );
    this.mobile_rangeMax?.addEventListener(
      "input",
      this.syncInputsFromSliderMobile,
    );
    this.mobile_minInput?.addEventListener(
      "change",
      this.syncSliderFromInputsMobile,
    );
    this.mobile_maxInput?.addEventListener(
      "change",
      this.syncSliderFromInputsMobile,
    );
  }
}

customElements.define("price-range-slider", PriceRangeSlider);

// Runs `callback(element)` once the element is near the viewport. Use it for layout-measuring
// setup (e.g. `new Swiper`) so it doesn't force a reflow during page load.
window.initWhenNearViewport = function (
  element,
  callback,
  rootMargin = "300px 0px",
) {
  if (!element) return;
  if (!("IntersectionObserver" in window)) {
    callback(element);
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      callback(element);
    },
    { rootMargin },
  );
  observer.observe(element);
};

if (!customElements.get("slider-component")) {
  // Swiper measures the slider on init. Initialising from an IntersectionObserver callback
  // reads an already-computed layout (no forced reflow) and skips off-screen sliders until needed.
  const sliderInitObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              entry.target.initSlider();
            });
          },
          { rootMargin: "300px 0px" },
        )
      : null;

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      if (sliderInitObserver) sliderInitObserver.observe(this);
      else this.initSlider();
    }

    disconnectedCallback() {
      sliderInitObserver?.unobserve(this);
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    initSlider() {
      if (this.swiper || !this.isConnected) return;
      let options = {};
      try {
        options = JSON.parse(this.dataset.swiperOptions || "{}");
      } catch (error) {
        console.error("Invalid data-swiper-options provided:", error);
      }
      // Provide reasonable defaults if not specified
      options = {
        slidesPerView: 1,
        spaceBetween: 16,
        pagination: { clickable: true },
        ...options,
      };

      const paginationEl = this.querySelector(".swiper-pagination");
      if (paginationEl) {
        options.pagination = {
          ...options.pagination,
          el: paginationEl,
        };
      }
      if (typeof Swiper === "function") {
        this.swiper = new Swiper(this, options);
        // Keep the active pagination dot in view when many bullets overflow horizontally.
        // Only the pagination row is scrolled: scrollIntoView() would also scroll the page and force a reflow on load.
        if (paginationEl) {
          const scrollActivePaginationBullet = () => {
            if (paginationEl.scrollWidth <= paginationEl.clientWidth) return;
            const active = paginationEl.querySelector(
              ".swiper-pagination-bullet-active",
            );
            if (!active) return;
            const offset =
              active.getBoundingClientRect().left -
              paginationEl.getBoundingClientRect().left +
              paginationEl.scrollLeft;
            paginationEl.scrollTo({
              left:
                offset - (paginationEl.clientWidth - active.offsetWidth) / 2,
              behavior: "smooth",
            });
          };
          this.swiper.on(
            "slideChangeTransitionEnd",
            scrollActivePaginationBullet,
          );
        }
        // Mark as ready so CSS can fade component in (prevents initial flicker)
        this.classList.add("is-ready");
      } else {
        console.error("Swiper library not loaded");
      }
    }
  }
  customElements.define("slider-component", SliderComponent);
}

if (!customElements.get("product-card")) {
  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(
        this.querySelector("[data-product-variants-json]").textContent,
      );
      this.variantSelector = this.querySelector("[data-variant-selector]");
      this.priceContainer = this.querySelector("[data-price-container]");
      this.savingPrice = 0;
      this.variantSelector?.addEventListener(
        "click",
        this.onVariantClick.bind(this),
      );
    }

    onVariantClick(event) {
      if (!event.target.matches("[data-variant-id]")) return;
      event.preventDefault();

      const selectedVariantId = event.target.dataset.variantId;
      const selectedVariant = this.variants.find(
        (v) => v.id == selectedVariantId,
      );

      if (selectedVariant) {
        this.updatePrice(selectedVariant);
        this.updateActivePill(event.target);
      }
    }

    updatePrice(variant) {
      const isMobileCard = this.querySelector(".details");
      const addToCartButton = this.querySelector("add-to-cart-button");
      const inStock = variant.available === true && Number(variant.price) > 0;

      const stockBadge = this.querySelector("[data-stock-badge]");
      if (stockBadge) {
        stockBadge.hidden = false;
        stockBadge.classList.toggle("stock-badge--sold-out", !inStock);
        stockBadge.textContent = inStock
          ? stockBadge.dataset.textInStock
          : stockBadge.dataset.textSoldOut;
      }

      if (this.priceContainer) this.priceContainer.hidden = !inStock;

      const deliveryRow = this.querySelector("[data-product-card-delivery]");
      if (deliveryRow) {
        const inStockBlock = deliveryRow.querySelector(
          "[data-delivery-in-stock]",
        );
        const soldOutBlock = deliveryRow.querySelector(
          "[data-delivery-sold-out]",
        );
        if (inStockBlock && soldOutBlock) {
          if (inStock) {
            deliveryRow.removeAttribute("hidden");
            deliveryRow.hidden = false;
            inStockBlock.removeAttribute("hidden");
            inStockBlock.hidden = false;
            soldOutBlock.setAttribute("hidden", "");
            soldOutBlock.hidden = true;
            document.dispatchEvent(new CustomEvent("delivery-estimate:update"));
          } else {
            inStockBlock.setAttribute("hidden", "");
            inStockBlock.hidden = true;
            soldOutBlock.setAttribute("hidden", "");
            soldOutBlock.hidden = true;
            deliveryRow.setAttribute("hidden", "");
          }
        } else if (inStock) {
          deliveryRow.removeAttribute("hidden");
          document.dispatchEvent(new CustomEvent("delivery-estimate:update"));
        } else {
          deliveryRow.setAttribute("hidden", "");
        }
      }

      if (addToCartButton) {
        addToCartButton.setAttribute("variant-id", variant.id);
        addToCartButton.hidden = !inStock;
        if (inStock) addToCartButton.removeAttribute("hidden");
        else addToCartButton.setAttribute("hidden", "");
        const cartBtn = addToCartButton.querySelector("button");
        if (cartBtn) {
          cartBtn.disabled = !inStock;
          cartBtn.classList.toggle("btn-unavailable-state", !inStock);
          const buttonLabel = cartBtn.querySelector(".button-label");
          const cartIcon = cartBtn.querySelector(".product-card-cart-icon");
          if (buttonLabel) {
            buttonLabel.textContent = inStock
              ? addToCartButton.dataset.textAddToCart
              : addToCartButton.dataset.textSoldOut;
          }
          if (cartIcon) {
            cartIcon.hidden = !inStock;
          }
        }
      }
      if (isMobileCard) {
        const price = (variant.price / 100).toLocaleString("nl-NL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        let priceHTML = `<sup class="text-extra-small font-semibold">€</sup> <span class="text-body-large font-semibold">${price}</span>`;
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          const compareAtPrice = (
            variant.compare_at_price / 100
          ).toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          priceHTML += ` <sup class="font-medium text-inactive" style="font-size: 10px;">€</sup> <s class="text-body font-medium text-inactive">${compareAtPrice}</s>`;
        }
        this.priceContainer.innerHTML = `<p>${priceHTML}</p>`;
      } else {
        const price = (variant.price / 100).toLocaleString("nl-NL", {
          style: "currency",
          currency: "EUR",
        });
        let priceHTML = `<span>${price}</span>`;
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          const compareAtPrice = (
            variant.compare_at_price / 100
          ).toLocaleString("nl-NL", {
            style: "currency",
            currency: "EUR",
          });
          priceHTML += ` <s class="compare-at-price">${compareAtPrice}</s>`;
        }
        this.priceContainer.innerHTML = `<p class="price">${priceHTML}</p>`;
      }
    }

    updateActivePill(selectedPill) {
      this.variantSelector
        .querySelectorAll("[data-variant-id]")
        .forEach((pill) => pill.classList.remove("active"));
      selectedPill.classList.add("active");
      this.savingPrice = selectedPill.getAttribute("data-sale-price");
      this.savingPriceEle = this.querySelector("[data-saving-price]");
      const numericPrice = parseFloat(
        this.savingPrice.replace(/[^0-9.-]+/g, ""),
      );

      if (this.savingPriceEle) {
        if (numericPrice === 0) {
          this.querySelector(".sale").classList.add("hidden");
        } else {
          this.querySelector(".sale").classList.remove("hidden");
          this.savingPriceEle.textContent = this.savingPrice;
        }
      }
    }
  }

  customElements.define("product-card", ProductCard);
}

document.addEventListener("DOMContentLoaded", () => {
  // Handle elements not inside a section
  const buttons = document.querySelectorAll(".toggle-spec-btn");
  buttons.forEach(function (btn) {
    btn.addEventListener("click", function () {
      var container = btn.closest(".prod-spec-container");
      if (!container) return;
      var prodSpec = container.querySelector(".prod-spec");
      if (!prodSpec) return;

      var expanded = prodSpec.classList.toggle("expanded");
      btn.innerHTML = expanded
        ? '<img src="https://cdn.shopify.com/s/files/1/0264/0540/4720/files/right.png?v=1752515896" style="transform: rotate(270deg); transition: transform 0.3s;" />' +
          btn.dataset.closeLabel
        : '<img src="https://cdn.shopify.com/s/files/1/0264/0540/4720/files/right.png?v=1752515896" />' +
          btn.dataset.openLabel;
    });
  });

  document.querySelector(".mobile-bar")?.addEventListener("click", function () {
    document.getElementById("mobileNav")?.classList.add("open");
    document.body.classList.add("no-scroll");
  });

  document
    .getElementById("closeMobileNav")
    ?.addEventListener("click", function () {
      document.getElementById("mobileNav").classList.remove("open");
      document.body.classList.remove("no-scroll");
      document
        .querySelectorAll(".mobile-nav-menu .has-submenu.open")
        .forEach((item) => {
          item.classList.remove("open");
          const link = item.querySelector(".submenu-toggle");
          if (link) link.setAttribute("aria-expanded", "false");
        });
    });

  document.querySelectorAll(".mobile-nav-menu .has-submenu").forEach((item) => {
    const toggle = item.querySelector(".submenu-chevron");
    const toggleLink = item.querySelector(".submenu-toggle");
    if (!toggle) return;
    const setExpanded = (open) => {
      if (toggleLink)
        toggleLink.setAttribute("aria-expanded", open ? "true" : "false");
    };
    setExpanded(item.classList.contains("open"));
    toggle.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      item.classList.toggle("open");
      setExpanded(item.classList.contains("open"));
    });
  });

  document
    .querySelector(".back-to-top")
    ?.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

  document
    .querySelectorAll(".filter-group.collapsible")
    .forEach(function (group) {
      var header = group.querySelector(".filter-group-header");
      var content = group.querySelector(".filter-options");
      var chevron = group.querySelector(".chevron");
      header.addEventListener("click", function () {
        var isOpen = group.classList.toggle("open");
        if (isOpen) {
          content.style.display = "block";
          chevron.style.transform = "rotate(0deg)";
        } else {
          content.style.display = "none";
          chevron.style.transform = "rotate(-180deg)";
        }
      });
      if (!group.classList.contains("open")) {
        content.style.display = "none";
        chevron.style.transform = "rotate(-180deg)";
      }
    });

  document
    .querySelectorAll(".more-filter-options")
    .forEach(function (filterGroup) {
      var moreToggle = filterGroup.querySelector(".more-toggle");
      var extra = filterGroup.querySelectorAll(".extra-item");
      var chevron = filterGroup.querySelector(".more-chevron");
      var text = filterGroup.querySelector(".more-text");
      var expanded = false;
      if (!moreToggle) return;
      moreToggle.addEventListener("click", function () {
        expanded = !expanded;
        extra.forEach(function (el) {
          el.style.display = expanded ? "flex" : "none";
        });
        chevron.style.transform = expanded ? "rotate(180deg)" : "rotate(0deg)";
        text.textContent = expanded ? "Less" : "More";
      });
    });
});

// Cart count logic
function updateCartCounts(count) {
  const cartItemCounts = document.querySelectorAll(".cart-item-count");
  cartItemCounts.forEach((cartItemCount) => {
    if (count > 0) {
      cartItemCount.innerText = count;
      cartItemCount.style.display = "flex";
      cartItemCount.classList.add("animate");
      cartItemCount.addEventListener(
        "animationend",
        () => {
          cartItemCount.classList.remove("animate");
        },
        { once: true },
      );
    } else {
      cartItemCount.style.display = "none";
    }
  });
}

function refreshCart() {
  fetch("/cart.js")
    .then((response) => response.json())
    .then((cart) => {
      updateCartCounts(cart.item_count);
    })
    .catch((error) => console.error("Error fetching cart:", error));
}

document.addEventListener("cart:updated", function () {
  refreshCart();
});

// Added-to-cart popup
function hideCartNotification() {
  const notification = document.querySelector("[data-cart-notification]");
  if (!notification) return;
  clearTimeout(notification.hideTimer);
  notification.classList.remove("is-visible");
  notification.setAttribute("aria-hidden", "true");
}

function showCartNotification(item) {
  const notification = document.querySelector("[data-cart-notification]");
  if (!notification) return;
  const productEl = notification.querySelector(
    "[data-cart-notification-product]",
  );
  if (productEl) {
    productEl.textContent = item?.title || item?.product_title || "";
    productEl.title = productEl.textContent;
  }
  notification.setAttribute("aria-hidden", "false");
  notification.classList.add("is-visible");
  clearTimeout(notification.hideTimer);
  notification.hideTimer = setTimeout(hideCartNotification, 4000);
}

document.addEventListener("click", function (event) {
  if (event.target.closest("[data-cart-notification-close]")) {
    hideCartNotification();
  }
});
if (!customElements.get("product-gallery")) {
  class ProductGallery extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.mainMediaContainer = this.querySelector(
        ".product-gallery__main-media",
      );
      this.thumbnails = this.querySelectorAll(".product-gallery__thumbnail");

      this.thumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener("click", this.onThumbnailClick.bind(this));
      });

      if (this.thumbnails.length > 0) {
        this.setActiveMedia(this.thumbnails[0].dataset.targetMediaId);
      }
    }

    onThumbnailClick(event) {
      const thumbnail = event.currentTarget;
      const targetMediaId = thumbnail.dataset.targetMediaId;
      this.setActiveMedia(targetMediaId);
    }

    setActiveMedia(mediaId) {
      const allMediaItems = this.mainMediaContainer.querySelectorAll(
        ".product-media-item",
      );
      allMediaItems.forEach((mediaItem) => {
        mediaItem.hidden = true;
        const video = mediaItem.querySelector("video, iframe");
        if (video) {
          if (video.tagName === "VIDEO") {
            video.pause();
          }
        }
      });

      const activeMediaItem = this.mainMediaContainer.querySelector(
        `.product-media-item[data-media-id="${mediaId}"]`,
      );
      if (activeMediaItem) {
        activeMediaItem.hidden = false;
        const activeVideo = activeMediaItem.querySelector("video");
        if (activeVideo) {
          activeVideo.play();
        }
      }

      this.thumbnails.forEach((thumbnail) => {
        if (thumbnail.dataset.targetMediaId === mediaId) {
          thumbnail.classList.add("is-active");
        } else {
          thumbnail.classList.remove("is-active");
        }
      });
    }
  }
  customElements.define("product-gallery", ProductGallery);
}

if (!customElements.get("product-recommendations")) {
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      this.dataset.loaded = "true";

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, "text/html");
          const results = html.querySelector(".related-container");
          const target = this.querySelector("[data-recommendations-results]");
          if (!results || !results.querySelector("product-card") || !target) {
            this.remove();
            return;
          }
          target.replaceWith(results);
          this.hidden = false;
        })
        .catch((error) => {
          console.error("Error loading product recommendations:", error);
          this.remove();
        });
    }
  }
  customElements.define("product-recommendations", ProductRecommendations);
}

if (!customElements.get("add-to-cart-button")) {
  class AddToCartButton extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector("button");
      this.originalButtonContent = this.button.innerHTML;
      this.addEventListener("click", this.addToCart.bind(this));
    }

    get variantId() {
      return this.getAttribute("variant-id");
    }

    async addToCart(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.button.disabled) return;

      const buttonText = this.querySelector(".button-text");
      const spinner = this.querySelector(".btn-spinner");

      this.button.disabled = true;
      this.button.classList.remove("btn-add-to-cart-error");
      this.button.classList.remove("btn-added-state");
      this.button.classList.add("is-loading");
      this.button.style.removeProperty("background-color");
      this.button.style.removeProperty("border-color");
      this.button.style.removeProperty("color");
      if (buttonText) buttonText.style.display = "none";
      if (spinner) spinner.style.display = "inline-flex";

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: this.variantId,
                quantity: 1,
              },
            ],
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! ${response.status}`);
        }

        const data = await response.json().catch(() => null);
        document.dispatchEvent(new CustomEvent("cart:updated"));
        showCartNotification(data?.items?.[0]);

        if (buttonText) {
          this.button.classList.remove("btn-add-to-cart-error");
          this.button.classList.add("btn-added-state");
          this.button.style.backgroundColor = "#28a745";
          this.button.style.color = "#fff";
          buttonText.textContent = this.dataset.textAddedToCart;
          buttonText.style.color = "#fff";
        }
      } catch (error) {
        console.error("Error adding to cart:", error);
        this.button.classList.remove("btn-added-state");
        this.button.classList.add("btn-add-to-cart-error");
        this.button.style.setProperty(
          "background-color",
          "rgba(198, 40, 40, 1)",
          "important",
        );
        this.button.style.setProperty("border-color", "#b71c1c", "important");
        this.button.style.setProperty("color", "#fff", "important");
        if (buttonText) {
          buttonText.textContent = this.dataset.textAddToCartError;
        }
      } finally {
        this.button.classList.remove("is-loading");
        if (buttonText) buttonText.style.display = "inline";
        if (spinner) spinner.style.display = "none";

        setTimeout(() => {
          this.button.disabled = false;
          this.button.classList.remove("btn-add-to-cart-error");
          this.button.classList.remove("btn-added-state");
          this.button.style.removeProperty("background-color");
          this.button.style.removeProperty("border-color");
          this.button.style.removeProperty("color");
          if (buttonText) {
            buttonText.style.removeProperty("color");
          }
          this.button.innerHTML = this.originalButtonContent;
        }, 2000);
      }
    }
  }
  customElements.define("add-to-cart-button", AddToCartButton);
}

document.querySelectorAll(".drawer-btn").forEach(function (btn) {
  btn.addEventListener("click", function () {
    let drawerType = btn.getAttribute("data-drawer-type");
    let drawer = document.getElementById(drawerType);

    if (drawer) {
      drawer.classList.add("open");
      document.body.classList.add("no-scroll");
      if (drawerType === "search_mobileNav") {
        drawer.setAttribute("aria-hidden", "false");
        var searchInput = drawer.querySelector('input[type="search"]');
        if (searchInput) {
          requestAnimationFrame(function () {
            searchInput.focus({ preventScroll: true });
          });
        }
      }
    }
  });
});

document
  .querySelectorAll(".mobile-nav .close-drawer")
  .forEach(function (closeBtn) {
    closeBtn.addEventListener("click", function () {
      let drawer = closeBtn.closest(".drawer-wrapper");
      if (drawer) {
        drawer.classList.remove("open");
        document.body.classList.remove("no-scroll");
        if (drawer.id === "search_mobileNav") {
          drawer.setAttribute("aria-hidden", "true");
        }
      }
    });
  });
